import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import { toUser } from "@/queries/auth"
import type { ApiResource } from "@/lib/api/types"
import type { ApiUser, Session } from "@/types/auth"
import type {
  AccountSession,
  EmailChangeChallenge,
  ApiAccountSession,
  Profile,
  RevokedSessions,
} from "@/types/profile"

function toAccountSession(session: ApiAccountSession): AccountSession {
  return {
    id: session.id,
    name: session.name,
    device: session.device,
    browser: session.browser,
    operatingSystem: session.operating_system,
    ipAddress: session.ip_address,
    lastActiveAt: session.last_active_at,
    createdAt: session.created_at,
    expiresAt: session.expires_at,
    isCurrent: session.is_current,
  }
}

async function fetchProfile(
  path: string,
  options: { method?: "PATCH" | "POST" | "PUT" | "DELETE"; body?: unknown }
): Promise<Profile> {
  const { data } = await apiFetch<ApiResource<ApiUser>>(path, {
    ...options,
  })
  return toUser(data)
}

export const profileQueries = {
  detail: () =>
    queryOptions({
      queryKey: apiKey("/profile"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiUser>>("/profile", {
          signal,
        })
        return toUser(data)
      },
    }),
  sessions: () =>
    queryOptions({
      queryKey: apiKey("/profile/sessions"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiAccountSession[]>>(
          "/profile/sessions",
          { signal }
        )
        return data.map(toAccountSession)
      },
    }),
}

function storeProfile(queryClient: QueryClient, profile: Profile) {
  queryClient.setQueryData(apiKey("/profile"), profile)
  queryClient.setQueryData<Session>(apiKey("/auth/session"), (session) =>
    session ? { ...session, user: profile } : session
  )
}

function useProfileMutation<TInput>(
  mutationFn: (input: TInput) => Promise<Profile>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (profile) => {
      storeProfile(queryClient, profile)
    },
  })
}

export function useUpdateProfile() {
  return useProfileMutation(
    async (input: { name: string }) =>
      await fetchProfile("/profile", { method: "PATCH", body: input })
  )
}

export function useUploadAvatar() {
  return useProfileMutation(async (avatar: File) => {
    const body = new FormData()
    body.set("avatar", avatar)

    return await fetchProfile("/profile/avatar", { method: "POST", body })
  })
}

export function useRemoveAvatar() {
  return useProfileMutation(
    async () => await fetchProfile("/profile/avatar", { method: "DELETE" })
  )
}

/**
 * Sends a six-digit code to the new address. The account keeps its current
 * address until the code is confirmed.
 */
export function useRequestEmailChange() {
  return useMutation({
    mutationFn: async (input: {
      email: string
      password: string
    }): Promise<EmailChangeChallenge> => {
      const response = await apiFetch<{ challenge_id: string; email: string }>(
        "/profile/email",
        { method: "POST", body: input }
      )
      return { challengeId: response.challenge_id, email: response.email }
    },
  })
}

export function useConfirmEmailChange() {
  return useProfileMutation(
    async (input: { challengeId: string; code: string }) =>
      await fetchProfile("/profile/email", {
        method: "PUT",
        body: { challenge_id: input.challengeId, code: input.code },
      })
  )
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: {
      currentPassword: string
      password: string
      passwordConfirmation: string
    }) => {
      await apiFetch<void>("/profile/password", {
        method: "PUT",
        body: {
          current_password: input.currentPassword,
          password: input.password,
          password_confirmation: input.passwordConfirmation,
        },
      })
    },
  })
}

export function useRevokeSession() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await apiFetch<void>(`/profile/sessions/${sessionId}`, {
        method: "DELETE",
      })
    },
  })
}

export function useRevokeOtherSessions() {
  return useMutation({
    mutationFn: async () =>
      await apiFetch<RevokedSessions>("/profile/sessions", {
        method: "DELETE",
      }),
  })
}
